// @flow
import type { Errors } from 'types/errors';
import type { RequestStates } from 'types/redux';
import type { Driver, Drivers, Location } from 'types/entities';
import type { Booking } from 'types/bookings';
import type { DriverLibraryState } from 'reducers/library';

import _ from 'lodash';
import classnames from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { cohortDriverAssociate } from 'actions/cohorts';
import {
  driverLogin,
  driverLogout,
  driverDelete,
  driverSetAvailability,
  driverSetAutoAccept,
  driverSetLocation,
  driverBookingBid,
  driverBookingCancel,
  driverBookingPickUp,
  driverBookingAdditionalDropOff,
  driverBookingDropOff,
  driverBookingDelete,
} from 'actions/drivers';
import {
  showDriversModal,
  showDriverLocationModal,
  showDriverEditModal,
  showDriverFareModal,
  closeDriverFareModal,
  setActiveBooking,
  unsetActiveBooking,
} from 'actions/app';
import { resetErrorState } from 'actions/helpers';
import { DRIVER_BEHAVIOURS_NAMES, hasDriverBehaviour } from 'utils/library-utils';
import { entityURLForCohort } from 'utils/cohort-utils';
import {
  bookingStateString,
  canBookingBeBid,
  canBookingBeCancelledByDriver,
  canBookingBePickedUp,
  canBookingBeDroppedOff,
  containsAdditionalDropOff,
} from 'utils/booking-utils';

import {
  autoAcceptValueOff,
  autoAcceptValueNA,
} from 'reducers/cohorts';

import Toggle from 'react-toggle';
import MultiToggle from 'react-multi-toggle';
import ErrorsNotification from 'components/others/ErrorNotification';
import PowerButton from 'components/buttons/PowerButton';
import DriversModal from './DriversModal';
import DriverEditModal from './DriverEditModal';
import DriverFareModal from './DriverFareModal';
import DriverLocationModal from './DriverLocationModal';
import './DriversPanel.scss';

type Props = {
  activeCohortID: string,
  activeBookingID: string,
  drivers: Drivers,
  libraryDrivers: DriverLibraryState,
  getMapCenter: Function,

  onDriverClick: Function,

  cohortDriverAssociate: Function,
  cohortDriverAssociateRequest: RequestStates,
  cohortDriverAssociateErrors: Errors,

  driverLogin: Function,
  driverLoginRequest: RequestStates,
  driverLoginErrors: Errors,

  driverLogout: Function,
  driverLogoutRequest: RequestStates,
  driverLogoutErrors: Errors,

  driverDelete: Function,
  driverDeleteRequest: RequestStates,
  driverDeleteErrors: Errors,

  driverSetAvailability: Function,
  driverSetAvailabilityRequest: RequestStates,
  driverSetAvailabilityErrors: Errors,

  driverSetAutoAccept: Function,
  driverSetAutoAcceptRequest: RequestStates,
  driverSetAutoAcceptErrors: Errors,

  driverSetLocation: Function,

  driverBookingBid: Function,
  driverBookingBidRequest: RequestStates,
  driverBookingBidErrors: Errors,

  driverBookingCancel: Function,
  driverBookingCancelRequest: RequestStates,
  driverBookingCancelErrors: Errors,

  driverBookingPickUp: Function,
  driverBookingPickUpRequest: RequestStates,
  driverBookingPickUpErrors: Errors,

  driverBookingAdditionalDropOff: Function,
  driverBookingAdditionalDropOffRequest: RequestStates,
  driverBookingAdditionalDropOffErrors: Errors,

  driverBookingDropOff: Function,
  driverBookingDropOffRequest: RequestStates,
  driverBookingDropOffErrors: Errors,

  driverBookingDelete: Function,

  showDriversModal: Function,
  isDriversModalActive: boolean,

  showDriverEditModal: Function,
  driverBeingEdited: string,

  showDriverLocationModal: Function,
  driverLocationBeingEdited: string,

  showDriverFareModal: Function,
  closeDriverFareModal: Function,
  driverCompletingBooking: Object,

  setActiveBooking: Function,
  unsetActiveBooking: Function,

  resetErrorState: Function,
};

type State = {
  isCollapsed: boolean,
};

const requestErrors = [
  'cohortDriverAssociate',
  'driverLogin',
  'driverLogout',
  'driverDelete',
  'driverSetAvailability',
  'driverSetAutoAccept',
  'driverBookingBid',
  'driverBookingCancel',
  'driverBookingPickUp',
  'driverBookingAdditionalDropOff',
  'driverBookingDropOff',
];

const autoAcceptValues = [
  {
    displayName: 'On',
    value: 1,
  },
  {
    displayName: 'NA',
    value: -1,
  },
  {
    displayName: 'Off',
    value: 0,
  },
];

export class DriversPanel extends Component {
  constructor(props: Props) {
    super(props);
    this.state = {
      isCollapsed: false,
    };
  }

  state: State;

  componentDidMount() {
    this.props.resetErrorState(requestErrors);
  }

  props: Props;

  render() {
    const numDrivers: number = Object.keys(this.props.drivers).length;
    return (
      <div className="entity-panel">
        <button
          className="button is-primary is-small is-pulled-right entity-add-button"
          title="Associate driver to cohort"
          aria-label="Associate driver to cohort"
          onClick={(event) => {
            event.stopPropagation();
            this.props.showDriversModal(true);
          }}
        >
          <span className="icon is-small" aria-hidden="true">
            <i className="fa fa-plus" />
          </span>
        </button>
        <h3 className="title section-title is-6 entity-panel-title">
          <button
            onClick={() => {
              this.setState({
                isCollapsed: !this.state.isCollapsed,
              });
            }}
          >
            <span className="icon is-small">
              <i
                className={classnames('fa fa-fw', {
                  'fa-chevron-down': !this.state.isCollapsed,
                  'fa-chevron-right': this.state.isCollapsed,
                })}
              />
            </span>{' '}
            Drivers {!!numDrivers && `(${numDrivers})`}
          </button>
        </h3>
        {this.props.isDriversModalActive &&
          <DriversModal
            {...this.props}
            handleClose={() => { this.props.showDriversModal(false); }}
            drivers={this.props.libraryDrivers}
          />
        }
        {!!this.props.driverBeingEdited &&
          <DriverEditModal
            cohortID={this.props.activeCohortID}
            driverID={this.props.driverBeingEdited}
            handleClose={() => { this.props.showDriverEditModal(null); }}
            showModal={!!this.props.driverBeingEdited}
          />
        }
        {!!this.props.driverLocationBeingEdited &&
          <DriverLocationModal
            cohortID={this.props.activeCohortID}
            driverID={this.props.driverLocationBeingEdited}
            driverSetLocation={this.props.driverSetLocation}
            handleClose={() => { this.props.showDriverLocationModal(null); }}
            showModal={!!this.props.driverLocationBeingEdited}
          />
        }
        {!!this.props.driverCompletingBooking &&
          <DriverFareModal
            driverID={this.props.driverCompletingBooking.driverID}
            bookingID={this.props.driverCompletingBooking.bookingID}
            handleClose={() => { this.props.closeDriverFareModal(); }}
            showModal={!!this.props.driverCompletingBooking}
            resetErrorState={this.props.resetErrorState}
            driverBookingDropOff={this.props.driverBookingDropOff}
            driverBookingDropOffErrors={this.props.driverBookingDropOffErrors}
            driverBookingDropOffRequest={this.props.driverBookingDropOffRequest}
          />
        }
        {!this.state.isCollapsed &&
          <div>
            {!_.isEmpty(this.props.driverLoginErrors) &&
              <ErrorsNotification
                errors={this.props.driverLoginErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('driverLogin');
                }}
              />
            }
            {!_.isEmpty(this.props.driverLogoutErrors) &&
              <ErrorsNotification
                errors={this.props.driverLogoutErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('driverLogout');
                }}
              />
            }
            {!_.isEmpty(this.props.driverDeleteErrors) &&
              <ErrorsNotification
                errors={this.props.driverDeleteErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('driverDelete');
                }}
              />
            }
            {!_.isEmpty(this.props.driverSetAvailabilityErrors) &&
              <ErrorsNotification
                errors={this.props.driverSetAvailabilityErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('driverSetAvailability');
                }}
              />
            }
            {!_.isEmpty(this.props.driverSetAutoAcceptErrors) &&
            <ErrorsNotification
              errors={this.props.driverSetAutoAcceptErrors}
              dismissible
              onClose={() => {
                this.props.resetErrorState('driverSetAutoAccept');
              }}
            />
            }
            {!_.isEmpty(this.props.driverBookingBidErrors) &&
              <ErrorsNotification
                errors={this.props.driverBookingBidErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('driverBookingBid');
                }}
              />
            }
            {!_.isEmpty(this.props.driverBookingCancelErrors) &&
              <ErrorsNotification
                errors={this.props.driverBookingCancelErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('driverBookingCancel');
                }}
              />
            }
            {!_.isEmpty(this.props.driverBookingPickUpErrors) &&
              <ErrorsNotification
                errors={this.props.driverBookingPickUpErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('driverBookingPickUp');
                }}
              />
            }
            {!_.isEmpty(this.props.driverBookingAdditionalDropOffErrors) &&
              <ErrorsNotification
                errors={this.props.driverBookingAdditionalDropOffErrors}
                dismissible
                onClose={() => {
                  this.props.resetErrorState('driverBookingAdditionalDropOff');
                }}
              />
            }
            <ul>
              {numDrivers ?
                Object.keys(this.props.drivers).map((driverID: string) => {
                  const driver: Driver = this.props.drivers[driverID];
                  const disableButtons: boolean = this.props.driverLoginRequest.isPending ||
                    this.props.driverLogoutRequest.isPending ||
                    this.props.driverDeleteRequest.isPending ||
                    this.props.driverSetAvailabilityRequest.isPending ||
                    this.props.driverSetAutoAcceptRequest.isPending ||
                    this.props.driverBookingBidRequest.isPending ||
                    this.props.driverBookingCancelRequest.isPending ||
                    this.props.driverBookingPickUpRequest.isPending ||
                    this.props.driverBookingAdditionalDropOffRequest.isPending ||
                    this.props.driverBookingDropOffRequest.isPending;
                  return (
                    <li key={driverID} className="entity-row">
                      <div className="field is-grouped is-pulled-right">
                        {driver.loggedIn &&
                          <div className="control">
                            <span title="Driver availability">
                              <span className="sr-only">Driver availability</span>
                              <Toggle
                                disabled={disableButtons}
                                checked={driver.available}
                                icons={false}
                                onChange={() => {
                                  this.props.driverSetAvailability(this.props.activeCohortID,
                                      driverID, !driver.available);
                                  this.props.resetErrorState(requestErrors);
                                }}
                              />
                            </span>
                          </div>
                        }
                        <div className="control">
                          <span title="Driver is logged in">
                            <PowerButton
                              disabled={disableButtons}
                              active={driver.loggedIn}
                              onClick={() => {
                                if (driver.loggedIn) {
                                  this.props.driverLogout(this.props.activeCohortID,
                                    driverID);
                                } else {
                                  const mapCenter: Object = this.props.getMapCenter();
                                  let location: Location = [mapCenter.longitude,
                                    mapCenter.latitude];
                                  if (driver.latitude && driver.longitude) {
                                    // Use driver's location if he has lat/lng.
                                    location = [driver.longitude, driver.latitude];
                                  }
                                  this.props.driverLogin(this.props.activeCohortID, driverID,
                                    driver, location);
                                }
                                this.props.resetErrorState(requestErrors);
                              }}
                              label={driver.loggedIn ? 'Logout driver' : 'Login driver'}
                            />
                          </span>
                        </div>
                        {driver.loggedIn &&
                          <p className="control">
                            <button
                              className={classnames('button is-small is-warning is-outlined', {
                                'is-disabled': disableButtons,
                              })}
                              title="Pan map to driver"
                              aria-label="Pan map to driver"
                              onClick={() => {
                                this.props.onDriverClick(driver);
                              }}
                            >
                              <span className="icon is-small" aria-hidden="true">
                                <i className="fa fa-crosshairs" />
                              </span>
                            </button>
                          </p>
                        }
                        {driver.loggedIn &&
                          <p className="control">
                            <button
                              className={classnames('button is-small is-success is-outlined', {
                                'is-disabled': disableButtons,
                              })}
                              title="Set driver to location via address"
                              aria-label="Set driver to location via address"
                              onClick={() => {
                                this.props.showDriverLocationModal(driverID);
                              }}
                            >
                              <span className="icon is-small" aria-hidden="true">
                                <i className="fa fa-map-marker" />
                              </span>
                            </button>
                          </p>
                        }
                        {!driver.loggedIn &&
                          <p className="control">
                            <button
                              className={classnames('button is-small is-info is-outlined', {
                                'is-disabled': disableButtons,
                              })}
                              title="Edit driver"
                              aria-label="Edit driver"
                              onClick={() => {
                                this.props.showDriverEditModal(driverID);
                              }}
                            >
                              <span className="icon is-small" aria-hidden="true">
                                <i className="fa fa-pencil" />
                              </span>
                            </button>
                          </p>
                        }
                        <p className="control">
                          <button
                            className={classnames('button is-small is-danger is-outlined', {
                              'is-disabled': disableButtons,
                              'is-loading': this.props.driverDeleteRequest.isPending,
                            })}
                            onClick={() => {
                              /* eslint-disable no-alert */
                              if (confirm(`Are you sure you want to remove Driver ${driverID} from the cohort?`)) {
                                this.props.driverDelete(this.props.activeCohortID,
                                  driverID);
                                this.props.resetErrorState(requestErrors);
                              }
                            }}
                            title="Unassociate driver"
                            aria-label="Unassociate driver"
                          >
                            <span className="icon is-small" aria-hidden="true">
                              <i className="fa fa-trash" />
                            </span>
                          </button>
                        </p>
                      </div>
                      <p className="entity-title">
                        Driver ID: {driverID}
                      </p>
                      <div className="entity-details-section">
                        <div>
                          Gamma ID: {driver.cohortDriverID}{' '}
                          <a
                            href={`${entityURLForCohort()}/drivers/${driver.cohortDriverID}`}
                            target="_blank"
                            title="View driver in Gamma"
                            aria-label="View driver in Gamma"
                            rel="noopener noreferrer"
                          >
                            <span className="icon is-small" aria-hidden="true">
                              <i className="fa fa-external-link" />
                            </span>
                          </a>
                          {' '}&middot;{' '}
                          {DRIVER_BEHAVIOURS_NAMES[_.get(driver, 'behaviour.type')]}
                        </div>
                        <div className="field is-grouped">
                          <div className="control">
                            <span title="Driver auto-accept status toggle">
                              <span className="sr-only">Driver auto-accept</span>
                              <MultiToggle
                                options={autoAcceptValues}
                                selectedOption={driver.autoAccept}
                                onSelectOption={(value) => {
                                  let flag;
                                  driver.autoAccept = value;
                                  if (value === autoAcceptValueNA) {
                                    return;
                                  }
                                  if (value > autoAcceptValueOff) {
                                    flag = true;
                                  } else {
                                    flag = false;
                                  }
                                  this.props.driverSetAutoAccept(
                                    this.props.activeCohortID, driverID, flag);
                                }}
                                label="Auto Accept:"
                              />
                            </span>
                          </div>
                        </div>
                        {!!driver.bookings && !!driver.bookings.length &&
                          <div>
                            <ul className="entity-bookings">
                              {driver.bookings.map((booking: Booking) => {
                                const bookingID: string = booking.bookingID;
                                return (
                                  <li
                                    key={bookingID}
                                    className={classnames('entity-booking-row', {
                                      active: this.props.activeBookingID === bookingID,
                                    })}
                                    onClick={() => {
                                      if (this.props.activeBookingID === bookingID) {
                                        this.props.unsetActiveBooking();
                                      } else {
                                        this.props.setActiveBooking({
                                          driverID,
                                          bookingID,
                                        });
                                      }
                                    }}
                                  >
                                    <div
                                      className="field is-grouped is-pulled-right"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                      }}
                                    >
                                      {canBookingBeBid(booking.state) &&
                                        <p className="control">
                                          <button
                                            className={classnames('button is-small is-primary is-outlined', {
                                              'is-disabled': disableButtons,
                                            })}
                                            onClick={() => {
                                              this.props.driverBookingBid(driverID, bookingID);
                                            }}
                                          >
                                            BID
                                          </button>
                                        </p>
                                      }
                                      {canBookingBePickedUp(booking.state) &&
                                        <p className="control">
                                          <button
                                            className={classnames('button is-small is-primary is-outlined', {
                                              'is-disabled': disableButtons,
                                            })}
                                            onClick={() => {
                                              this.props.driverBookingPickUp(driverID, bookingID);
                                            }}
                                          >
                                            PICK UP
                                          </button>
                                        </p>
                                      }
                                      {canBookingBeDroppedOff(booking.state) && containsAdditionalDropOff(booking) &&
                                          !this.props.driverBookingAdditionalDropOffRequest.isSuccessful &&
                                          <p className="control">
                                            <button
                                              className={classnames('button is-small is-primary is-outlined', {
                                                'is-disabled': disableButtons,
                                              })}
                                              onClick={() => {
                                                this.props.driverBookingAdditionalDropOff(driverID, bookingID, 0, 0,
                                                    0, 1);
                                              }}
                                            >
                                             1ST DROP OFF
                                            </button>
                                          </p>
                                      }
                                      {canBookingBeDroppedOff(booking.state) && containsAdditionalDropOff(booking) &&
                                      <p className="control">
                                        <button
                                          className={classnames('button is-small is-primary is-outlined', {
                                            'is-disabled': disableButtons,
                                          })}
                                          onClick={() => {
                                            this.props.showDriverFareModal(driverID, bookingID);
                                          }}
                                        >
                                          2ND DROP OFF
                                        </button>
                                      </p>
                                      }
                                      {canBookingBeDroppedOff(booking.state) && !containsAdditionalDropOff(booking) &&
                                      <p className="control">
                                        <button
                                          className={classnames('button is-small is primary is-outlined', {
                                            'is-disabled': disableButtons,
                                          })}
                                          onClick={() => {
                                            this.props.showDriverFareModal(driverID, bookingID);
                                          }}
                                        >
                                           DROP OFF
                                        </button>
                                      </p>
                                      }
                                      {!hasDriverBehaviour(_.get(driver, 'behaviour.type')) &&
                                        canBookingBeCancelledByDriver(booking.state) &&
                                        <p className="control">
                                          <button
                                            className={classnames('button is-small is-warning is-outlined', {
                                              'is-disabled': disableButtons,
                                            })}
                                            onClick={() => {
                                              this.props.driverBookingCancel(driverID, bookingID);
                                            }}
                                          >
                                            CANCEL
                                          </button>
                                        </p>
                                      }
                                      <p className="control">
                                        <button
                                          className="button is-small is-outlined is-danger"
                                          onClick={() => {
                                            this.props.driverBookingDelete(
                                              this.props.activeCohortID, driverID, bookingID);
                                          }}
                                        >
                                          <span className="icon is-small">
                                            <i className="fa fa-trash" title="Remove booking" />
                                          </span>
                                        </button>
                                      </p>
                                    </div>
                                    <p className="entity-title">{bookingID}</p>
                                    <p>
                                      <span className="tag is-small">
                                        {bookingStateString(booking.state)}
                                      </span>
                                      {booking.passengerID &&
                                        <span>
                                          {' '}&middot;{' '}
                                          Passenger {booking.passengerID}
                                        </span>
                                      }
                                    </p>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        }
                      </div>
                    </li>
                  );
                })
                :
                <li className="entity-row">No drivers associated to this cohort.</li>
              }
            </ul>
          </div>
        }
      </div>
    );
  }
}

function mapStateToProps(state) {
  const activeCohortID: string = state.app.activeCohortID;
  const activeBookingID: string = _.get(state.app, 'activeBooking.bookingID');
  const libraryDrivers: DriverLibraryState = state.library.drivers || {};
  const drivers: Drivers = _.get(state.cohorts[activeCohortID], 'drivers', {});
  const cohortDriverIDs: Array<string> = _.map(drivers, (pax: Driver) => pax.cohortDriverID);
  // Remove the already associated drivers.
  const filteredLibraryDrivers: Drivers = _.omit(libraryDrivers, cohortDriverIDs);

  return {
    activeCohortID,
    activeBookingID,
    isDriversModalActive: state.app.showDriversModal,
    driverBeingEdited: state.app.driverBeingEdited,
    driverLocationBeingEdited: state.app.driverLocationBeingEdited,
    driverCompletingBooking: state.app.driverCompletingBooking,

    libraryDrivers: filteredLibraryDrivers,
    drivers,

    cohortDriverAssociateRequest: state.requests.cohortDriverAssociateRequest || {},
    cohortDriverAssociateErrors: _.get(state.errors, 'cohortDriverAssociate.errors', []),

    driverLoginRequest: state.requests.driverLoginRequest || {},
    driverLoginErrors: _.get(state.errors, 'driverLogin.errors', []),

    driverLogoutRequest: state.requests.driverLogoutRequest || {},
    driverLogoutErrors: _.get(state.errors, 'driverLogout.errors', []),

    driverDeleteRequest: state.requests.driverDeleteRequest || {},
    driverDeleteErrors: _.get(state.errors, 'driverDelete.errors', []),

    driverSetAvailabilityRequest: state.requests.driverSetAvailabilityRequest || {},
    driverSetAvailabilityErrors: _.get(state.errors, 'driverSetAvailability.errors', []),

    driverSetAutoAcceptRequest: state.requests.driverSetAutoAcceptRequest || {},
    driverSetAutoAcceptErrors: _.get(state.errors, 'driverSetAutoAccept.errors', []),

    driverBookingBidRequest: state.requests.driverBookingBidRequest || {},
    driverBookingBidErrors: _.get(state.errors, 'driverBookingBid.errors', []),

    driverBookingCancelRequest: state.requests.driverBookingCancelRequest || {},
    driverBookingCancelErrors: _.get(state.errors, 'driverBookingCancel.errors', []),

    driverBookingPickUpRequest: state.requests.driverBookingPickUpRequest || {},
    driverBookingPickUpErrors: _.get(state.errors, 'driverBookingPickUp.errors', []),

    driverBookingAdditionalDropOffRequest: state.requests.driverBookingAdditionalDropOffRequest || {},
    driverBookingAdditionalDropOffErrors: _.get(state.errors, 'driverBookingAdditionalDropOff.errors', []),

    driverBookingDropOffRequest: state.requests.driverBookingDropOffRequest || {},
    driverBookingDropOffErrors: _.get(state.errors, 'driverBookingDropOff.errors', []),
  };
}

export default connect(
  mapStateToProps,
  {
    cohortDriverAssociate,
    driverLogin,
    driverLogout,
    driverDelete,
    driverSetAvailability,
    driverSetAutoAccept,
    driverSetLocation,

    driverBookingBid,
    driverBookingCancel,
    driverBookingPickUp,
    driverBookingAdditionalDropOff,
    driverBookingDropOff,
    driverBookingDelete,

    showDriversModal,
    showDriverEditModal,
    showDriverLocationModal,
    showDriverFareModal,
    closeDriverFareModal,

    setActiveBooking,
    unsetActiveBooking,

    resetErrorState,
  }
)(DriversPanel);



/** WEBPACK FOOTER **
 ** ./src/js/views/dashboard/drivers/DriversPanel.jsx
 **/